// src/pages/SupplierFsvpPacketPage.js
// Printable FSVP compliance packet for one manufacturer — see docs/SUPPLIERS_MODULE.md.
// Not a merged PDF: one page listing checklist status + notes, with each attached file one
// click away. Follows the same print-page pattern as BolPrintPage.js.
import React, { useState, useEffect } from 'react';
import supplierService from '../services/supplierService';
import { usePermissions } from '../context/PermissionContext';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d + 'T12:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
}

export default function SupplierFsvpPacketPage() {
  const { companySettings } = usePermissions();
  const [supplier, setSupplier] = useState(null);
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');

  const params = new URLSearchParams(window.location.search);
  const supplierId = params.get('id');

  useEffect(() => {
    if (!supplierId) { setError('No supplier specified.'); return; }
    Promise.all([
      supplierService.getSupplier(supplierId),
      supplierService.getFsvpChecklist(supplierId),
    ])
      .then(([s, checklist]) => { setSupplier(s); setItems(checklist); })
      .catch(() => setError('Could not load FSVP packet.'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const companyName = companySettings?.name || 'Company';
  const logoUrl = companySettings?.logoUrl || null;

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">{error}</div>
        <button className="btn btn-secondary" onClick={() => window.close()}>Close</button>
      </div>
    );
  }

  if (!supplier || !items) {
    return (
      <div className="container mt-5 text-center">
        <span className="spinner-border" />
      </div>
    );
  }

  const applicableItems = items.filter(i => i.status !== 'not_applicable');
  const completedCount = applicableItems.filter(i => i.status === 'completed').length;
  const naCount = items.length - applicableItems.length;
  const address = [supplier.addressLine1, supplier.addressLine2, supplier.city, supplier.stateProvince, supplier.postalCode, supplier.country]
    .filter(Boolean).join(', ');

  return (
    <div>
      {/* Screen-only toolbar — hidden when printing */}
      <div className="d-print-none" style={{ background: '#1b2638', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12, position: 'sticky', top: 0, zIndex: 2000 }}>
        <span style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>FSVP Packet — {supplier.name}</span>
        <div className="ms-auto d-flex gap-2">
          <button className="btn btn-primary btn-sm" onClick={() => window.print()}>Print</button>
          <button className="btn btn-outline-light btn-sm" onClick={() => window.close()}>Close</button>
        </div>
      </div>

      {/* Print content */}
      <div style={{ maxWidth: 860, margin: '24px auto', background: '#fff', padding: '32px 40px', boxShadow: '0 2px 12px rgba(0,0,0,0.1)', fontFamily: 'Arial, sans-serif', color: '#000' }}
           className="d-print-block">
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          {logoUrl
            ? <img
                src={logoUrl}
                alt={companyName}
                style={{ maxHeight: 70, maxWidth: 320, objectFit: 'contain', display: 'block', margin: '0 auto' }}
              />
            : <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: 4 }}>{companyName.toUpperCase()}</div>
          }
          <div style={{ fontSize: 14, fontWeight: 600, marginTop: 6 }}>FSVP Compliance Record</div>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, fontSize: 12 }}>
          <tbody>
            <tr>
              <td style={{ width: '50%', paddingRight: 12, verticalAlign: 'top' }}>
                <div style={{ fontWeight: 700 }}>Foreign Supplier</div>
                <div>{supplier.name}</div>
                {address && <div>{address}</div>}
                {supplier.contactName && <div>Contact: {supplier.contactName}</div>}
              </td>
              <td style={{ width: '50%', verticalAlign: 'top' }}>
                <div style={{ fontWeight: 700 }}>Status</div>
                <div>{completedCount} of {applicableItems.length} checklist items complete{naCount > 0 ? ` (${naCount} N/A)` : ''}</div>
                <div>Generated {fmtDate(new Date().toISOString().split('T')[0])}</div>
              </td>
            </tr>
          </tbody>
        </table>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #000' }}>
              <th style={{ textAlign: 'left', padding: '4px 4px', width: 28 }}></th>
              <th style={{ textAlign: 'left', padding: '4px 4px' }}>Checklist Item</th>
              <th style={{ textAlign: 'left', padding: '4px 4px' }}>Completed By</th>
              <th style={{ textAlign: 'left', padding: '4px 4px' }}>Notes</th>
              <th style={{ textAlign: 'left', padding: '4px 4px' }}>Evidence</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.templateId} style={{ borderBottom: '1px solid #ccc' }}>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>
                  {item.status === 'completed' ? '✓' : (item.status === 'not_applicable' ? 'N/A' : '—')}
                </td>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>
                  <div style={{ fontWeight: 600 }}>{item.label}</div>
                  {item.description && <div style={{ color: '#555', fontSize: 10 }}>{item.description}</div>}
                </td>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>{item.completedByName || '—'}</td>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>{item.notes || '—'}</td>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>
                  {item.documents.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {item.documents.map(doc => (
                        <a key={doc.id} href={doc.fileUrl} target="_blank" rel="noreferrer">
                          {doc.name}{doc.expiryDate ? ` (exp. ${doc.expiryDate})` : ''}
                        </a>
                      ))}
                    </div>
                  ) : (item.status !== 'not_applicable' && item.requiresDocument ? <span style={{ color: '#b00' }}>Missing</span> : '—')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <style>{`
        @media print {
          body { margin: 0; }
          div[style*="maxWidth: 860px"], div[style*="max-width: 860px"] {
            box-shadow: none !important;
            margin: 0 !important;
            padding: 10mm 12mm !important;
          }
          a { color: #000 !important; text-decoration: underline; }
        }
      `}</style>
    </div>
  );
}
